import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import type { PortalForecastRow } from '@proerp/shared';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@proerp/ui';
import { formatQuantity } from '@/lib/formatForecast';
import { buildDemandSeries, UNIT_FALLBACK } from '@/lib/portalMetrics';
import { prefersReducedMotion } from '@/lib/theme';

/**
 * Bedarf je Monat, **gestapelt nach Einheit** (ADR 0085).
 *
 * Die Stapelung ist keine Optik: Über Artikel hinweg lassen sich Mengen nicht
 * addieren („500 Stk + 40 kg" ist keine Zahl). Jede Einheit bekommt daher ihr
 * eigenes Segment — der Balken zeigt die Zusammensetzung eines Monats, nicht
 * eine erfundene Gesamtmenge.
 *
 * Die Farben kommen aus den Chart-Tokens des Design-Systems und funktionieren
 * damit in beiden Themes. Auf die Farbe allein verlässt sich nichts: Legende
 * und Tooltip nennen die Einheit im Klartext (WCAG — Farbe nie als einziger
 * Träger einer Information).
 */
const UNIT_COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)'];

export function DemandByMonthChart({ rows }: { rows: readonly PortalForecastRow[] }) {
  const series = buildDemandSeries(rows);
  // recharts animiert per JavaScript — die CSS-Regel in `index.css` greift dort
  // nicht. Wer Bewegung abgeschaltet hat, bekommt sonst wachsende Balken.
  const isAnimated = !prefersReducedMotion();

  if (series.points.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        {series.undatedCount > 0
          ? 'Für den Verlauf fehlen Termine — alle Positionen sind noch ohne Datum geplant.'
          : 'Noch keine Mengen mit Termin.'}
      </p>
    );
  }

  const config: ChartConfig = Object.fromEntries(
    series.units.map((unit, index) => [
      unit,
      { label: unit === UNIT_FALLBACK ? 'ohne Einheit' : unit, color: UNIT_COLORS[index % UNIT_COLORS.length] },
    ]),
  );

  return (
    <div className="flex flex-col gap-2">
      <ChartContainer config={config} className="aspect-auto h-[260px] w-full">
        <BarChart data={series.points} margin={{ left: 4, right: 4, top: 8 }}>
          <CartesianGrid vertical={false} />
          <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} minTickGap={12} />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={48}
            tickFormatter={(value: number) => formatQuantity(value)}
          />
          <ChartTooltip
            cursor={{ fill: 'var(--muted)' }}
            content={
              <ChartTooltipContent
                formatter={(value, name) => (
                  <div className="flex w-full items-center justify-between gap-4">
                    <span className="text-muted-foreground">
                      {name === UNIT_FALLBACK ? 'ohne Einheit' : String(name)}
                    </span>
                    <span className="font-medium tabular-nums text-foreground">
                      {formatQuantity(Number(value))}
                    </span>
                  </div>
                )}
              />
            }
          />
          {series.units.length > 1 && <ChartLegend content={<ChartLegendContent />} />}
          {series.units.map((unit, index) => (
            <Bar
              key={unit}
              dataKey={unit}
              stackId="menge"
              fill={UNIT_COLORS[index % UNIT_COLORS.length]}
              // Nur der oberste Balken wird gerundet — sonst entstehen bei
              // gestapelten Segmenten sichtbare Lücken.
              radius={index === series.units.length - 1 ? [4, 4, 0, 0] : 0}
              isAnimationActive={isAnimated}
            />
          ))}
        </BarChart>
      </ChartContainer>

      {series.undatedCount > 0 && (
        <p className="text-xs text-muted-foreground">
          {series.undatedCount === 1
            ? 'Eine Position ist noch ohne Termin und deshalb nicht im Verlauf enthalten.'
            : `${series.undatedCount} Positionen sind noch ohne Termin und deshalb nicht im Verlauf enthalten.`}
        </p>
      )}
    </div>
  );
}
